import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

export interface SearchableDropdownProps {
  options: string[];
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  defaultLabel?: string;
  className?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  defaultLabel = 'All',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-full flex items-center justify-between px-[12px] bg-[#fbfbfa] border border-solid border-[#e9e9e9] rounded-[9px] text-[13px] font-medium text-[#111111] transition-all hover:border-[#111111] focus:outline-none"
      >
        <span className="truncate pr-2">{value || defaultLabel}</span>
        <ChevronDown className="w-4 h-4 text-[#9C9C9A] shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute top-[calc(100%+4px)] left-0 w-[240px] bg-[#fbfbfa] border border-slate-200 rounded-[9px] shadow-lg z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={placeholder}
                className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border-none rounded-md text-[13px] focus:outline-none focus:ring-1 focus:ring-slate-200 placeholder:text-slate-400"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-[240px] overflow-y-auto p-1 custom-scrollbar">
            <button
              onClick={() => {
                onChange(defaultLabel);
                setIsOpen(false);
                setSearchQuery('');
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-left text-[13px] rounded-md transition-colors ${
                value === defaultLabel || !value
                  ? 'bg-slate-100 font-semibold text-slate-900'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span>{defaultLabel}</span>
              {(value === defaultLabel || !value) && <Check className="w-4 h-4" />}
            </button>
            {filteredOptions.map((opt, idx) => {
              const isSelected = value === opt;
              return (
                <button
                  key={`${opt}-${idx}`}
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                    setSearchQuery('');
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-[13px] rounded-md transition-colors ${
                    isSelected
                      ? 'bg-slate-100 font-semibold text-slate-900'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span className="truncate pr-2">{opt}</span>
                  {isSelected && <Check className="w-4 h-4" />}
                </button>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-slate-500">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
